import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import type { SDataTableColumnDef } from '@modules/ui-components/dialogs/SDataTable';
import { SDataTable } from '@modules/ui-components/dialogs/SDataTable';
import type {
  CreativeDecisionPortfolioResult,
  CreativeDecisionPortfolioSort,
  CreativeDecisionFilterFacet,
  CreativeDecisionFilterFacets,
  CreativeDecisionProjection,
  CreativeDecisionCopilotAnswer,
  CreativeDecisionCopilotAnswerRequest,
  CreativeDataState,
  CreativeDecisionState,
  CreativeDecisionMaturityState,
  CreativeDeploymentScope,
  CreativeLifecycleStage,
  CreativeOrigin,
  CreativeReportEnrichmentItem,
  ProviderSourceReportEnrichmentItem,
  EvidenceClass,
  MoneyAmountBinding,
  MonetizationContextRef,
  PaidAdsProvider,
} from '../../creativeDecisions';
import type { FunnelInsightsApiClient } from '../../api/insightsClient';
import { buildCreativeDecisionFilterFacets } from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';
import { CreativeDecisionDrawer, DecisionChip } from './CreativeDecisionDrawer';
import { CreativeReportEnrichmentThumbnail } from './CreativeReportEnrichment';
import { CreativeProviderSourceThumbnail } from './CreativeProviderSourceEnrichment';
import {
  CREATIVE_REPORT_LOCALE_ALL,
  CREATIVE_REPORT_LOCALE_UNKNOWN,
  CreativeLocaleDimensionAnalysis,
  creativeReportLocaleOptions,
  filterCreativeDecisionsByLocale,
} from './CreativeLocaleAnalysis';
import {
  formatMoneyBinding,
  formatPercent,
  formatRoas,
  formatSummaryMoney,
  humanizeCreativeReasonCode,
  valueBasisLabel,
} from './creativeDecisionFormat';

export interface CreativeDecisionPortfolioFilters {
  providers: PaidAdsProvider[];
  providerAccountIds: string[];
  lifecycleStages: CreativeLifecycleStage[];
  dataStates: CreativeDataState[];
  decisions: CreativeDecisionState[];
  evidenceClasses: EvidenceClass[];
  origins: CreativeOrigin[];
  campaignFamilies: string[];
  networks: string[];
  funnels: string[];
  geos: string[];
  purchaseModes: MonetizationContextRef['purchaseMode'][];
  subscriptionEntries: MonetizationContextRef['subscriptionEntry'][];
  productIds: string[];
  planIds: string[];
  priceIds: string[];
  originalCurrencies: string[];
  reportingCurrencies: string[];
  maturityStates: CreativeDecisionMaturityState[];
  /** Backward-compatible URL-only filter. New UI uses explicit currency bases. */
  currencies: string[];
}

export const EMPTY_CREATIVE_DECISION_PORTFOLIO_FILTERS: CreativeDecisionPortfolioFilters = {
  providers: [],
  providerAccountIds: [],
  lifecycleStages: [],
  dataStates: [],
  decisions: [],
  evidenceClasses: [],
  origins: [],
  campaignFamilies: [],
  networks: [],
  funnels: [],
  geos: [],
  purchaseModes: [],
  subscriptionEntries: [],
  productIds: [],
  planIds: [],
  priceIds: [],
  originalCurrencies: [],
  reportingCurrencies: [],
  maturityStates: [],
  currencies: [],
};

const EMPTY_FILTERS = EMPTY_CREATIVE_DECISION_PORTFOLIO_FILTERS;

const PROVIDER_OPTIONS: PaidAdsProvider[] = ['meta', 'google_ads', 'tiktok_ads'];
const LIFECYCLE_OPTIONS: CreativeLifecycleStage[] = [
  'hypothesis', 'produced', 'launched', 'learning', 'mature', 'scaling', 'fatiguing', 'refreshed', 'archived',
];
const DATA_STATE_OPTIONS: CreativeDataState[] = [
  'available', 'no_data', 'too_young', 'unreconciled', 'pending_settlement', 'stale', 'blocked_by_trust',
];
const DECISION_OPTIONS: CreativeDecisionState[] = [
  'observe', 'hold', 'investigate', 'controlled_scale', 'refresh_due', 'stop_candidate', 'archived',
];
const EVIDENCE_OPTIONS: EvidenceClass[] = ['descriptive', 'predictive', 'randomized', 'incremental'];
const MATURITY_OPTIONS: CreativeDecisionMaturityState[] = ['immature', 'mature', 'unknown'];
const ORIGIN_OPTIONS: CreativeOrigin[] = ['connected_account', 'segmently_created', 'unknown'];
const PURCHASE_MODE_OPTIONS: MonetizationContextRef['purchaseMode'][] = ['subscription', 'one_time', 'unknown'];
const SUBSCRIPTION_ENTRY_OPTIONS: MonetizationContextRef['subscriptionEntry'][] = [
  'none', 'no_trial', 'free_trial', 'paid_trial', 'unknown',
];

interface CreativeDecisionPortfolioProps {
  portfolio: CreativeDecisionPortfolioResult | null;
  loading?: boolean;
  error?: string | null;
  sort: CreativeDecisionPortfolioSort;
  onSortChange: (sort: CreativeDecisionPortfolioSort) => void;
  search?: string;
  onSearchChange?: (search: string) => void;
  draftFilters?: CreativeDecisionPortfolioFilters;
  appliedFilterCount?: number;
  onFiltersChange?: (filters: CreativeDecisionPortfolioFilters) => void;
  onApplyFilters?: () => void;
  onClearFilters?: () => void;
  loadingMore?: boolean;
  loadMoreError?: string | null;
  onLoadMore?: () => void;
  detailDecision?: CreativeDecisionProjection | null;
  detailLoading?: boolean;
  detailError?: string | null;
  enrichmentByDeploymentKey?: Readonly<Record<string, CreativeReportEnrichmentItem | undefined>>;
  enrichmentLoading?: boolean;
  enrichmentError?: string | null;
  providerSourceByDeploymentKey?: Readonly<Record<string, ProviderSourceReportEnrichmentItem | undefined>>;
  providerSourceLoading?: boolean;
  providerSourceError?: string | null;
  projectId?: string;
  providerSourceAssociationWindow?: { from: string; to: string };
  onProviderSourceSynced?: () => void;
  sourceSyncClient?: Pick<
    FunnelInsightsApiClient,
    'preflightProviderCreativeSourceReportRow' | 'syncMetaCreativeSources' | 'getProviderCreativeSourceSync'
  >;
  localeFilter?: string;
  onLocaleFilterChange?: (locale: string) => void;
  onOpenDecision?: (deploymentKey: string) => void;
  onRetryDetail?: () => void;
  onCloseDecision?: () => void;
  onRetry?: () => void;
  onConnectAdAccount?: () => void;
  /** Applied deployment scope; the chip reflects the SERVER echo, not hope. */
  deploymentScope?: CreativeDeploymentScope;
  onDeploymentScopeChange?: (scope: CreativeDeploymentScope) => void;
  onPrimaryAction?: (decision: CreativeDecisionProjection) => void;
  onAskEvidence?: (request: CreativeDecisionCopilotAnswerRequest) => Promise<CreativeDecisionCopilotAnswer>;
  /** Cached-first manual refresh (topup plan S1); shown only with a serving marker. */
  onRefreshSnapshot?: () => Promise<void>;
  /** I5b banner state-2 CTA: re-sync Meta delivery over the portfolio window. */
  onResyncDelivery?: () => Promise<void>;
  /**
   * I3 deep link: opens the drawer for this deployment on mount. Works from
   * BOTH scopes — the detail endpoint is unscoped, so the drawer resolves
   * even when the row is hidden from the visible portfolio page.
   */
  deeplinkDeploymentKey?: string;
}

export function CreativeDecisionPortfolio({
  portfolio,
  loading = false,
  error,
  sort,
  onSortChange,
  search = '',
  onSearchChange,
  draftFilters = EMPTY_FILTERS,
  appliedFilterCount = 0,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  loadingMore = false,
  loadMoreError,
  onLoadMore,
  detailDecision,
  detailLoading = false,
  detailError,
  enrichmentByDeploymentKey = {},
  enrichmentLoading = false,
  enrichmentError,
  providerSourceByDeploymentKey = {},
  providerSourceLoading = false,
  providerSourceError,
  projectId,
  providerSourceAssociationWindow,
  onProviderSourceSynced,
  sourceSyncClient,
  localeFilter = CREATIVE_REPORT_LOCALE_ALL,
  onLocaleFilterChange,
  onOpenDecision,
  onRetryDetail,
  onCloseDecision,
  onRetry,
  onConnectAdAccount,
  deploymentScope = 'funnel_linked',
  onDeploymentScopeChange,
  onPrimaryAction,
  onAskEvidence,
  onRefreshSnapshot,
  onResyncDelivery,
  deeplinkDeploymentKey,
}: CreativeDecisionPortfolioProps): React.ReactElement {
  const theme = useTheme();
  const expandedTable = useMediaQuery(theme.breakpoints.up('xl'));
  const [selectedDeploymentKey, setSelectedDeploymentKey] = React.useState<string | null>(deeplinkDeploymentKey ?? null);
  const [filtersExpanded, setFiltersExpanded] = React.useState(false);
  const effectiveDraftFilters = React.useMemo(
    () => ({ ...EMPTY_FILTERS, ...draftFilters }),
    [draftFilters],
  );
  const searchFilteredRows = React.useMemo(
    () => filterLoadedRows(portfolio?.rows ?? [], search),
    [portfolio?.rows, search],
  );
  const filteredRows = React.useMemo(
    () => filterCreativeDecisionsByLocale(
      searchFilteredRows,
      enrichmentByDeploymentKey,
      localeFilter,
      enrichmentLoading,
    ),
    [enrichmentByDeploymentKey, enrichmentLoading, localeFilter, searchFilteredRows],
  );
  const localeOptions = React.useMemo(
    () => creativeReportLocaleOptions(enrichmentByDeploymentKey, localeFilter),
    [enrichmentByDeploymentKey, localeFilter],
  );
  const filterFacets = React.useMemo(
    () => portfolio?.filterFacets ?? buildCreativeDecisionFilterFacets(portfolio?.rows ?? []),
    [portfolio?.filterFacets, portfolio?.rows],
  );
  const hasSelectedProviderCoverage = Boolean(
    portfolio?.sourceCoverage.providerCoverage?.some((entry) => entry.selectedByRequest),
  );
  const selected = React.useMemo(
    () => {
      const summary = portfolio?.rows.find(
        (row) => row.deployment.deploymentKey === selectedDeploymentKey,
      ) ?? null;
      if (detailDecision?.deployment.deploymentKey !== selectedDeploymentKey) return summary;
      const summaryContextCount = summary?.artifactContext?.deploymentContexts?.length ?? 0;
      const detailContextCount = detailDecision.artifactContext?.deploymentContexts?.length ?? 0;
      return summaryContextCount > detailContextCount
        ? { ...detailDecision, artifactContext: summary?.artifactContext }
        : detailDecision;
    },
    [detailDecision, portfolio?.rows, selectedDeploymentKey],
  );
  const openDecision = React.useCallback((row: CreativeDecisionProjection) => {
    const deploymentKey = row.deployment.deploymentKey;
    setSelectedDeploymentKey(deploymentKey);
    onOpenDecision?.(deploymentKey);
  }, [onOpenDecision]);
  const columns = React.useMemo<SDataTableColumnDef<CreativeDecisionProjection>[]>(
    () => buildColumns(
      openDecision,
      expandedTable,
      enrichmentByDeploymentKey,
      enrichmentLoading,
      providerSourceByDeploymentKey,
      providerSourceLoading,
    ),
    [
      enrichmentByDeploymentKey,
      enrichmentLoading,
      expandedTable,
      openDecision,
      providerSourceByDeploymentKey,
      providerSourceLoading,
    ],
  );

  React.useEffect(() => {
    if (
      selectedDeploymentKey
      && portfolio
      && !portfolio.rows.some((row) => row.deployment.deploymentKey === selectedDeploymentKey)
      // Deep-link invariant (I3): a drawer resolved through the UNSCOPED
      // detail endpoint stays open even when its row is hidden from the
      // visible portfolio (other scope, other page). Only close when the
      // selection has neither a row NOR detail evidence behind it.
      && detailDecision?.deployment.deploymentKey !== selectedDeploymentKey
      && !detailLoading
    ) {
      setSelectedDeploymentKey(null);
    }
  }, [portfolio, selectedDeploymentKey, detailDecision, detailLoading]);

  if (loading && !portfolio) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ minHeight: 360 }} data-testid={CREATIVE_DECISIONS_TEST_IDS.loading}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">Loading connected-account decisions…</Typography>
      </Stack>
    );
  }

  if (error && !portfolio) {
    return (
      <Alert
        severity="error"
        data-testid={CREATIVE_DECISIONS_TEST_IDS.error}
        action={onRetry ? <Button color="inherit" onClick={onRetry}>Retry</Button> : undefined}
      >
        {error}
      </Alert>
    );
  }

  if (
    portfolio
    && portfolio.rows.length === 0
    && (appliedFilterCount > 0 || search.trim())
    // A requested provider is itself a server filter, but its typed coverage
    // state is more informative than the generic "no matches" escape hatch.
    // Preserve that evidence for disabled, disconnected, stale, and
    // projection-ineligible connectors instead of hiding it behind filters.
    && !hasSelectedProviderCoverage
  ) {
    return (
      <Box data-testid={CREATIVE_DECISIONS_TEST_IDS.page} sx={{ p: 4, border: 1, borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>No deployments match these filters</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mb: 2 }}>
          Clear the server filters or loaded-row search to return to the connected-account portfolio.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            onSearchChange?.('');
            onClearFilters?.();
          }}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.filterClear}
        >
          Clear filters
        </Button>
      </Box>
    );
  }

  if (!portfolio || portfolio.rows.length === 0) {
    const providerCoverage = portfolio?.sourceCoverage.providerCoverage ?? [];
    return (
      <Box data-testid={CREATIVE_DECISIONS_TEST_IDS.empty} sx={{ p: 4, border: 1, borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {providerCoverage.some((entry) => entry.selectedByRequest && entry.state !== 'no_data')
            ? 'No decision-ready provider data'
            : 'No observable ad deployments'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mb: 2 }}>
          Connect a paid-ad account to make provider-native deployments visible. Creatives do not need to be created or imported in Segmently.
        </Typography>
        {providerCoverage.length > 0 && (
          <Stack spacing={0.75} sx={{ maxWidth: 640, mx: 'auto', mb: 2, textAlign: 'left' }}>
            {providerCoverage.map((entry) => (
              <Alert key={entry.provider} severity={providerCoverageSeverity(entry.state)}>
                {providerCoverageLabel(entry.provider, entry.state, entry.reasonCodes)}
              </Alert>
            ))}
          </Stack>
        )}
        {onConnectAdAccount && <Button variant="contained" onClick={onConnectAdAccount}>Connect ad account</Button>}
      </Box>
    );
  }

  return (
    <Box data-testid={CREATIVE_DECISIONS_TEST_IDS.page}>
      <Summary portfolio={portfolio} onRefreshSnapshot={onRefreshSnapshot} onResyncDelivery={onResyncDelivery} />

      <ProviderCoverageNotices portfolio={portfolio} />

      <CreativeLocaleDimensionAnalysis
        rows={portfolio.rows}
        enrichmentByDeploymentKey={enrichmentByDeploymentKey}
        loading={enrichmentLoading}
      />

      {enrichmentError && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {`Exact creative previews are temporarily unavailable: ${enrichmentError}. Performance metrics remain visible.`}
        </Alert>
      )}

      {providerSourceError && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {`Synced provider-source previews are temporarily unavailable: ${providerSourceError}. Performance metrics remain visible.`}
        </Alert>
      )}

      {error && (
        <Alert severity="warning" sx={{ mt: 2 }} action={onRetry ? <Button color="inherit" onClick={onRetry}>Retry</Button> : undefined}>
          {`Showing the last usable portfolio. Refresh failed: ${error}`}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mt: 2.5, mb: 1.5 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 750 }}>Creative decisions</Typography>
          <Typography variant="body2" color="text.secondary">
            Provider deployments are independent decision units. Active money flow determines the default attention order.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Chip
            clickable
            variant="outlined"
            color={deploymentScope === 'funnel_linked' ? 'primary' : 'default'}
            icon={deploymentScope === 'funnel_linked' ? <CheckIcon sx={{ fontSize: 16 }} /> : undefined}
            label="Web funnel scope"
            aria-pressed={deploymentScope === 'funnel_linked'}
            onClick={() => onDeploymentScopeChange?.(
              deploymentScope === 'funnel_linked' ? 'all' : 'funnel_linked',
            )}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.scopeToggle}
          />
          <TextField
            select
            size="small"
            label="Deployment locale"
            value={localeFilter}
            onChange={(event) => onLocaleFilterChange?.(event.target.value)}
            sx={{ minWidth: { sm: 190 } }}
          >
            <MenuItem value={CREATIVE_REPORT_LOCALE_ALL}>All deployment locales</MenuItem>
            <MenuItem value={CREATIVE_REPORT_LOCALE_UNKNOWN}>Unknown / provider-only</MenuItem>
            {localeOptions.map((locale) => (
              <MenuItem key={locale} value={locale}>{locale}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Search loaded decisions"
            value={search}
            onChange={(event) => onSearchChange?.(event.target.value)}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.search}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.75, color: 'text.secondary', fontSize: 19 }} /> }}
            sx={{ minWidth: { sm: 240 } }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersExpanded((value) => !value)}
            aria-expanded={filtersExpanded}
            aria-controls={CREATIVE_DECISIONS_TEST_IDS.filterPanel}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.filterToggle}
          >
            {`Filters${appliedFilterCount ? ` (${appliedFilterCount})` : ''}`}
          </Button>
        </Stack>
      </Stack>

      <Collapse in={filtersExpanded}>
        <FilterPanel
          filters={effectiveDraftFilters}
          facets={filterFacets}
          onChange={onFiltersChange}
          onApply={onApplyFilters}
          onClear={onClearFilters}
        />
      </Collapse>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={sort}
          onChange={(_event, value: CreativeDecisionPortfolioSort | null) => value && onSortChange(value)}
          aria-label="Creative decision ordering"
        >
          <ToggleButton value="money_flow" data-testid={CREATIVE_DECISIONS_TEST_IDS.sortMoneyFlow}>
            <AttachMoneyIcon sx={{ mr: 0.5, fontSize: 18 }} /> Money flow
          </ToggleButton>
          <ToggleButton value="needs_action" data-testid={CREATIVE_DECISIONS_TEST_IDS.sortNeedsAction}>
            <ErrorOutlineIcon sx={{ mr: 0.5, fontSize: 18 }} /> Needs action
          </ToggleButton>
          <ToggleButton value="historical_spend" data-testid={CREATIVE_DECISIONS_TEST_IDS.sortHistoricalSpend}>
            <HistoryIcon sx={{ mr: 0.5, fontSize: 18 }} /> Historical spend
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {search.trim() && filteredRows.length === 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          No loaded deployments match this search. Load more to search additional provider rows, or clear the search.
        </Alert>
      )}

      {localeFilter !== CREATIVE_REPORT_LOCALE_ALL && enrichmentLoading && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Resolving exact deployment bindings. The locale filter will apply when this loaded batch is ready.
        </Alert>
      )}

      {localeFilter !== CREATIVE_REPORT_LOCALE_ALL && !enrichmentLoading && filteredRows.length === 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          No loaded deployments match this locale. Metrics remain available under All deployment locales.
        </Alert>
      )}

      <Box data-testid={CREATIVE_DECISIONS_TEST_IDS.table}>
        <SDataTable<CreativeDecisionProjection>
          columns={columns}
          data={filteredRows}
          getRowId={(row) => row.deployment.deploymentKey}
          enableSorting={false}
          enableTopToolbar={false}
          enablePagination={portfolio.rows.length > 25}
          enableBottomToolbar={portfolio.rows.length > 25}
          initialState={{ pagination: { pageIndex: 0, pageSize: 25 } }}
          muiTableProps={{
            size: 'small',
            'aria-label': 'creative decisions table',
            sx: { minWidth: expandedTable ? 1340 : 900 },
          }}
          muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 340px)', minHeight: 260 } }}
          muiTableBodyRowProps={({ row }) => ({
            hover: true,
            onClick: () => openDecision(row.original),
            'data-testid': CREATIVE_DECISIONS_TEST_IDS.row(row.original.deployment.deploymentKey),
            sx: { cursor: 'pointer' },
          })}
        />
      </Box>

      <Stack alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" data-testid={CREATIVE_DECISIONS_TEST_IDS.loadedCount}>
          {search.trim() || localeFilter !== CREATIVE_REPORT_LOCALE_ALL
            ? `${filteredRows.length.toLocaleString()} matching · ${portfolio.rows.length.toLocaleString()} loaded of ${portfolio.page.totalRows.toLocaleString()}`
            : `${portfolio.rows.length.toLocaleString()} loaded of ${portfolio.page.totalRows.toLocaleString()}`}
        </Typography>
        {loadMoreError && (
          <Alert
            severity="warning"
            data-testid={CREATIVE_DECISIONS_TEST_IDS.loadMoreError}
            action={onLoadMore ? (
              <Button color="inherit" onClick={onLoadMore} data-testid={CREATIVE_DECISIONS_TEST_IDS.loadMoreRetry}>Retry</Button>
            ) : undefined}
          >
            {`Could not load the next page: ${loadMoreError}`}
          </Alert>
        )}
        {portfolio.page.nextCursor && !loadMoreError && onLoadMore && (
          <Button
            variant="outlined"
            onClick={onLoadMore}
            disabled={loadingMore}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.loadMore}
            startIcon={loadingMore ? <CircularProgress size={16} /> : undefined}
          >
            {loadingMore ? 'Loading more…' : 'Load more'}
          </Button>
        )}
      </Stack>

      <CreativeDecisionDrawer
        decision={selected}
        enrichmentItem={selectedDeploymentKey ? enrichmentByDeploymentKey[selectedDeploymentKey] : undefined}
        enrichmentLoading={Boolean(selectedDeploymentKey && enrichmentLoading)}
        providerSourceItem={selectedDeploymentKey ? providerSourceByDeploymentKey[selectedDeploymentKey] : undefined}
        providerSourceLoading={Boolean(selectedDeploymentKey && providerSourceLoading)}
        projectId={projectId}
        providerSourceAssociationWindow={providerSourceAssociationWindow}
        onProviderSourceSynced={onProviderSourceSynced}
        sourceSyncClient={sourceSyncClient}
        detailLoading={Boolean(selectedDeploymentKey && detailLoading)}
        detailError={selectedDeploymentKey ? detailError : null}
        onRetryDetail={onRetryDetail}
        onClose={() => {
          setSelectedDeploymentKey(null);
          onCloseDecision?.();
        }}
        onPrimaryAction={onPrimaryAction}
        onAskEvidence={onAskEvidence}
      />
    </Box>
  );
}

function ProviderCoverageNotices({
  portfolio,
}: {
  portfolio: CreativeDecisionPortfolioResult;
}): React.ReactElement | null {
  const notices = (portfolio.sourceCoverage.providerCoverage ?? []).filter((entry) => (
    entry.state === 'needs_reconnect'
    || entry.state === 'projection_unavailable'
    || (entry.selectedByRequest && entry.state !== 'available' && entry.state !== 'no_data')
  ));
  if (notices.length === 0) return null;
  return (
    <Stack spacing={1} sx={{ mt: 1.5 }}>
      {notices.map((entry) => (
        <Alert key={entry.provider} severity={providerCoverageSeverity(entry.state)}>
          {providerCoverageLabel(entry.provider, entry.state, entry.reasonCodes)}
        </Alert>
      ))}
    </Stack>
  );
}

function providerCoverageSeverity(
  state: NonNullable<CreativeDecisionPortfolioResult['sourceCoverage']['providerCoverage']>[number]['state'],
): 'info' | 'warning' | 'error' | 'success' {
  if (state === 'available') return 'success';
  if (state === 'needs_reconnect' || state === 'projection_unavailable') return 'warning';
  return 'info';
}

function providerCoverageLabel(
  provider: PaidAdsProvider,
  state: NonNullable<CreativeDecisionPortfolioResult['sourceCoverage']['providerCoverage']>[number]['state'],
  reasonCodes: string[],
): string {
  const name = provider === 'meta' ? 'Meta' : provider === 'google_ads' ? 'Google Ads' : 'TikTok Ads';
  const detail = reasonCodes[0]?.replaceAll('_', ' ');
  switch (state) {
    case 'available': return `${name}: provider deployments are available.`;
    case 'no_data': return `${name}: no deployments were observed in this window${detail ? ` (${detail})` : ''}.`;
    case 'not_connected': return `${name}: no connected reporting account.`;
    case 'needs_reconnect': return `${name}: reconnect the reporting account before relying on fresh decisions.`;
    case 'connector_disabled': return `${name}: the connector is not activated for this environment.`;
    case 'projection_unavailable': return `${name}: connector data is not yet eligible for the Creative Decisions projection${detail ? ` (${detail})` : ''}.`;
    default: return `${name}: provider coverage is unavailable.`;
  }
}

function FilterPanel({
  filters,
  facets,
  onChange,
  onApply,
  onClear,
}: {
  filters: CreativeDecisionPortfolioFilters;
  facets: CreativeDecisionFilterFacets;
  onChange?: (filters: CreativeDecisionPortfolioFilters) => void;
  onApply?: () => void;
  onClear?: () => void;
}): React.ReactElement {
  const update = <K extends keyof CreativeDecisionPortfolioFilters>(
    key: K,
    value: CreativeDecisionPortfolioFilters[K],
  ): void => onChange?.({ ...filters, [key]: value });
  return (
    <Box
      id={CREATIVE_DECISIONS_TEST_IDS.filterPanel}
      data-testid={CREATIVE_DECISIONS_TEST_IDS.filterPanel}
      role="region"
      aria-label="Creative decision filters"
      sx={{ p: 2, mb: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
        <MultiFilter
          label="Provider"
          values={filters.providers}
          options={PROVIDER_OPTIONS}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterProvider}
          onChange={(values) => update('providers', values as PaidAdsProvider[])}
        />
        <TextField
          size="small"
          label="Provider account IDs"
          value={filters.providerAccountIds.join(', ')}
          onChange={(event) => update('providerAccountIds', parseCommaList(event.target.value))}
          helperText="Comma-separated exact IDs"
          data-testid={CREATIVE_DECISIONS_TEST_IDS.filterAccount}
        />
        <MultiFilter
          label="Lifecycle"
          values={filters.lifecycleStages}
          options={LIFECYCLE_OPTIONS}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterLifecycle}
          onChange={(values) => update('lifecycleStages', values as CreativeLifecycleStage[])}
        />
        <MultiFilter
          label="Data state"
          values={filters.dataStates}
          options={DATA_STATE_OPTIONS}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterDataState}
          onChange={(values) => update('dataStates', values as CreativeDataState[])}
        />
        <MultiFilter
          label="Decision"
          values={filters.decisions}
          options={DECISION_OPTIONS}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterDecision}
          onChange={(values) => update('decisions', values as CreativeDecisionState[])}
        />
        <MultiFilter
          label="Evidence class"
          values={filters.evidenceClasses}
          options={EVIDENCE_OPTIONS}
          facet={facets.evidenceClass}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterEvidenceClass}
          onChange={(values) => update('evidenceClasses', values as EvidenceClass[])}
        />
        <MultiFilter
          label="Origin"
          values={filters.origins}
          options={ORIGIN_OPTIONS}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterOrigin}
          onChange={(values) => update('origins', values as CreativeOrigin[])}
        />
        <MultiFilter
          label="Campaign family"
          values={filters.campaignFamilies}
          options={facets.campaignFamily.values}
          facet={facets.campaignFamily}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterCampaignFamily}
          onChange={(values) => update('campaignFamilies', values)}
        />
        <MultiFilter
          label="Network"
          values={filters.networks}
          options={facets.network.values}
          facet={facets.network}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterNetwork}
          onChange={(values) => update('networks', values)}
        />
        <MultiFilter
          label="Funnel"
          values={filters.funnels}
          options={facets.funnel.values}
          facet={facets.funnel}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterFunnel}
          onChange={(values) => update('funnels', values)}
        />
        <MultiFilter
          label="Geo"
          values={filters.geos}
          options={facets.geo.values}
          facet={facets.geo}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterGeo}
          onChange={(values) => update('geos', values)}
        />
        <MultiFilter
          label="Purchase mode"
          values={filters.purchaseModes}
          options={PURCHASE_MODE_OPTIONS}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterPurchaseMode}
          onChange={(values) => update('purchaseModes', values as MonetizationContextRef['purchaseMode'][])}
        />
        <MultiFilter
          label="Trial / subscription entry"
          values={filters.subscriptionEntries}
          options={SUBSCRIPTION_ENTRY_OPTIONS}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterSubscriptionEntry}
          onChange={(values) => update('subscriptionEntries', values as MonetizationContextRef['subscriptionEntry'][])}
        />
        <MultiFilter
          label="Product"
          values={filters.productIds}
          options={facets.product.values}
          facet={facets.product}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterProduct}
          onChange={(values) => update('productIds', values)}
        />
        <MultiFilter
          label="Plan"
          values={filters.planIds}
          options={facets.plan.values}
          facet={facets.plan}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterPlan}
          onChange={(values) => update('planIds', values)}
        />
        <MultiFilter
          label="Price"
          values={filters.priceIds}
          options={facets.price.values}
          facet={facets.price}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterPrice}
          onChange={(values) => update('priceIds', values)}
        />
        <MultiFilter
          label="Original currency"
          values={filters.originalCurrencies}
          options={facets.originalCurrency.values}
          facet={facets.originalCurrency}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterOriginalCurrency}
          onChange={(values) => update('originalCurrencies', values.map((value) => value.toUpperCase()))}
        />
        <MultiFilter
          label="Reporting currency"
          values={filters.reportingCurrencies}
          options={facets.reportingCurrency.values}
          facet={facets.reportingCurrency}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterReportingCurrency}
          onChange={(values) => update('reportingCurrencies', values.map((value) => value.toUpperCase()))}
        />
        <MultiFilter
          label="Maturity"
          values={filters.maturityStates}
          options={MATURITY_OPTIONS}
          facet={facets.maturity}
          testId={CREATIVE_DECISIONS_TEST_IDS.filterMaturity}
          onChange={(values) => update('maturityStates', values as CreativeDecisionMaturityState[])}
        />
      </Box>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button onClick={onClear} data-testid={CREATIVE_DECISIONS_TEST_IDS.filterClear}>Clear</Button>
        <Button variant="contained" onClick={onApply} data-testid={CREATIVE_DECISIONS_TEST_IDS.filterApply}>Apply filters</Button>
      </Stack>
    </Box>
  );
}

function MultiFilter({
  label,
  values,
  options,
  testId,
  facet,
  onChange,
}: {
  label: string;
  values: readonly string[];
  options: readonly string[];
  testId: string;
  facet?: CreativeDecisionFilterFacet;
  onChange: (values: string[]) => void;
}): React.ReactElement {
  const labelId = `${testId}-label`;
  const unavailable = facet?.state === 'unavailable';
  const helper = facet?.state === 'unavailable'
    ? 'Unavailable for this portfolio scope'
    : facet?.state === 'partial'
      ? 'Available for some deployments'
      : undefined;
  return (
    <FormControl size="small" disabled={unavailable}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        multiple
        labelId={labelId}
        label={label}
        value={[...values]}
        onChange={(event) => onChange(typeof event.target.value === 'string' ? parseCommaList(event.target.value) : event.target.value)}
        renderValue={(selected) => selected.map(humanize).join(', ')}
        data-testid={testId}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={values.includes(option)} />
            <ListItemText primary={humanize(option)} />
          </MenuItem>
        ))}
      </Select>
      {helper && (
        <FormHelperText data-testid={CREATIVE_DECISIONS_TEST_IDS.filterAvailability(testId)}>
          {helper}
        </FormHelperText>
      )}
    </FormControl>
  );
}

function Summary({
  portfolio,
  onRefreshSnapshot,
  onResyncDelivery,
}: {
  portfolio: CreativeDecisionPortfolioResult;
  onRefreshSnapshot?: () => Promise<void>;
  onResyncDelivery?: () => Promise<void>;
}): React.ReactElement {
  const [resyncing, setResyncing] = React.useState(false);
  const buckets = portfolio.summary.reportingCurrencyBucket
    ? [portfolio.summary.reportingCurrencyBucket]
    : portfolio.summary.nativeCurrencyBuckets;
  const moneyPolicy = portfolio.summary.moneyReportingPolicy;
  // I5b: the server-typed registry state selects the banner copy — the client
  // never infers registration from row data (UNLOCK-AC-042). Cached payloads
  // predating the field have no registryState and keep the legacy state-1
  // copy: telling an operator to (re-)register is the safe default.
  const policyRegistered = moneyPolicy?.registryState === 'registered';
  const resync = async (): Promise<void> => {
    if (!onResyncDelivery || resyncing) return;
    setResyncing(true);
    try {
      await onResyncDelivery();
    } finally {
      setResyncing(false);
    }
  };
  return (
    <Box data-testid={CREATIVE_DECISIONS_TEST_IDS.summary}>
      {moneyPolicy?.state === 'unbound' && !policyRegistered && (
        // AC-H-8 / I5b state 1: registry EMPTY — the policy fail-closed state
        // is a NAMED project-level condition with its fix, never an
        // unexplained empty aggregate.
        <Alert severity="warning" data-testid={CREATIVE_DECISIONS_TEST_IDS.moneyPolicyBanner} sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Money reporting policy not configured</Typography>
          Cross-currency aggregates — contribution, projected value, global money ranking — are
          withheld, not zero. Register a money-reporting policy for this project; rows unlock
          automatically after the next nightly run. Per-ad numbers in a single currency stay live below.
        </Alert>
      )}
      {moneyPolicy?.state === 'unbound' && policyRegistered && (
        // I5b state 2: policy REGISTERED, spend rows not yet bound — the
        // measured prod defect showed state 1's «register» copy here. Copy
        // names the self-heal path and its deadline; the CTA accelerates it.
        <Alert
          severity="warning"
          data-testid={CREATIVE_DECISIONS_TEST_IDS.moneyPolicyBanner}
          sx={{ mb: 1.5 }}
          action={onResyncDelivery ? (
            <Button
              size="small"
              color="inherit"
              disabled={resyncing}
              onClick={() => { void resync(); }}
              data-testid={CREATIVE_DECISIONS_TEST_IDS.moneyPolicyResync}
            >
              {resyncing ? 'Re-syncing…' : 'Re-sync delivery'}
            </Button>
          ) : undefined}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Policy registered — spend rows not yet bound</Typography>
          The money-reporting policy{moneyPolicy.policyVersion ? ` ${moneyPolicy.policyVersion}` : ''} is
          active, but the spend rows in this window are not bound to it yet, so cross-currency
          aggregates stay withheld. Re-sync delivery now, or they bind automatically on
          tonight&apos;s sync. Per-ad numbers in a single currency stay live below.
        </Alert>
      )}
      {moneyPolicy?.state === 'partial' && (
        // I5b state 3: partial is a WORKING state, not an incident — one muted
        // caption line, never an Alert (severity ladder; UNLOCK-AC-043 names
        // both counts, collapsed into neither extreme).
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1 }}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.moneyPolicyPartialLine}
        >
          {`${moneyPolicy.unboundRows} of ${moneyPolicy.moneyRows} money rows not yet bound`}
          {moneyPolicy.policyVersion ? ` to ${moneyPolicy.policyVersion}` : ''}
          {' — totals below reflect bound rows only · binds on tonight’s sync'}
        </Typography>
      )}
      {!portfolio.summary.globalMoneyOrderAvailable && (
        <Alert severity="warning" data-testid={CREATIVE_DECISIONS_TEST_IDS.globalOrderBlocked} sx={{ mb: 1.5 }}>
          Global money ordering is unavailable: {humanizeCreativeReasonCode(portfolio.summary.globalMoneyOrderReason)}. Rows remain grouped by native currency/account.
        </Alert>
      )}
      <Stack spacing={1.25}>
        {buckets.map((bucket) => (
          <Box
            key={`${bucket.originalCurrency}:${bucket.currencyExponent}`}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.summaryBucket(bucket.originalCurrency)}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
              gap: 1,
            }}
          >
            <SummaryTile label="Spend in scope" value={formatSummaryMoney(bucket, 'selectedWindowSpendMinor')} />
            <SummaryTile label="Current / day" value={formatSummaryMoney(bucket, 'currentDailySpendMinor')} />
            <SummaryTile label="Needs attention" value={formatSummaryMoney(bucket, 'spendNeedingAttentionMinor')} tone="warning" />
            <SummaryTile label="Next 7 days" value={formatSummaryMoney(bucket, 'unchangedPaceSevenDayExposureMinor')} />
            <SummaryTile label="Unreconciled" value={formatSummaryMoney(bucket, 'unreconciledSpendMinor')} tone="muted" caption="provider not yet verified" />
            {/* W-A D4 (DISC-AC-012): the three withheld-spend classes render
                DISTINCTLY, each with its own cause — the old merged bucket
                erased self-healing vs operator-action. */}
            <SummaryTile
              label="Pending settlement"
              value={formatSummaryMoney(bucket, 'pendingSettlementSpendMinor')}
              tone="muted"
              caption="self-heals as provider days close"
              testId={CREATIVE_DECISIONS_TEST_IDS.summaryPendingSettlement}
            />
            <SummaryTile
              label="Trust-blocked"
              value={formatSummaryMoney(bucket, 'trustBlockedSpendMinor')}
              tone="warning"
              caption="required evidence lane missing — needs action"
              testId={CREATIVE_DECISIONS_TEST_IDS.summaryTrustBlocked}
            />
          </Box>
        ))}
      </Stack>
      <SnapshotBanner
        coverage={portfolio.sourceCoverage}
        onRefreshSnapshot={onRefreshSnapshot}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1 }}
        data-testid={CREATIVE_DECISIONS_TEST_IDS.outOfScopeFooter}
      >
        {coverageFooter(portfolio.sourceCoverage)}
      </Typography>
      <BoundedDecisionQueue rows={portfolio.rows} />
    </Box>
  );
}

/**
 * Cached-first honesty banner (topup plan S1, mock self-approved in
 * `.design/creative-decisions-scope/`): states WHICH data age the user sees.
 * Snapshot-served → «period X · computed Y · snapshot» + Refresh; live on the
 * canonical path → «computed just now · live query». No serving marker
 * (parameterized request or older payload) → nothing renders. Refresh NEVER
 * blanks served data — the numbers stay while the button spins.
 */
function SnapshotBanner({
  coverage,
  onRefreshSnapshot,
}: {
  coverage: CreativeDecisionPortfolioResult['sourceCoverage'];
  onRefreshSnapshot?: () => Promise<void>;
}): React.ReactElement | null {
  const [refreshing, setRefreshing] = React.useState(false);
  if (coverage.servedFrom === undefined) return null;
  const period = coverage.snapshotWindowFrom && coverage.snapshotWindowTo
    ? `period ${coverage.snapshotWindowFrom}–${coverage.snapshotWindowTo} · `
    : '';
  // AC-C-12: staleness beyond 2 producer cadences (>24h) is STATED, not left
  // to the reader to compute from the timestamp.
  const staleHours = coverage.servedFrom === 'snapshot' && coverage.snapshotComputedAt
    ? (Date.now() - new Date(coverage.snapshotComputedAt).getTime()) / 3_600_000
    : 0;
  const isStale = staleHours > 24;
  const label = coverage.servedFrom === 'snapshot'
    ? `${period}computed ${coverage.snapshotComputedAt
      ? new Date(coverage.snapshotComputedAt).toLocaleString()
      : '—'} · snapshot${isStale ? ` · stale (${Math.floor(staleHours)} h old) — refresh` : ''}`
    : 'computed just now · live query';
  const refresh = async (): Promise<void> => {
    if (!onRefreshSnapshot || refreshing) return;
    setRefreshing(true);
    try {
      await onRefreshSnapshot();
    } finally {
      setRefreshing(false);
    }
  };
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mt: 1 }}
      data-testid={CREATIVE_DECISIONS_TEST_IDS.portfolioSnapshotBanner}
    >
      <Typography variant="caption" color={isStale ? 'warning.main' : 'text.secondary'}>{label}</Typography>
      {coverage.servedFrom === 'snapshot' && onRefreshSnapshot && (
        <Button
          size="small"
          variant="outlined"
          disabled={refreshing}
          startIcon={refreshing ? <CircularProgress size={12} /> : undefined}
          onClick={() => { void refresh(); }}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.portfolioSnapshotRefresh}
          sx={{ minHeight: 0, py: 0, px: 1, fontSize: 12, textTransform: 'none' }}
        >
          Refresh
        </Button>
      )}
    </Stack>
  );
}

const MAX_SUMMARY_ALERTS = 3;

function BoundedDecisionQueue({ rows }: { rows: CreativeDecisionProjection[] }): React.ReactElement | null {
  const [expanded, setExpanded] = React.useState(false);
  const actionable = rows.filter((row) => !['observe', 'archived'].includes(row.recommendation.decision));
  if (actionable.length === 0) return null;
  const visible = actionable.slice(0, MAX_SUMMARY_ALERTS);
  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>Top actionable money flows</Typography>
          <Typography variant="caption" color="text.secondary">
            {`Showing ${visible.length} of ${actionable.length}; the queue is capped at ${MAX_SUMMARY_ALERTS} in the first viewport.`}
          </Typography>
        </Box>
        <Button size="small" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
          {expanded ? 'Hide complete trust list' : 'View complete trust list'}
        </Button>
      </Stack>
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {visible.map((row) => (
          <Stack key={row.deployment.deploymentKey} direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" noWrap>
              {row.display?.deploymentName ?? row.deployment.providerResourceName}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              {`${formatMoneyBinding(row.money.currentDailySpend, true)} / day · ${row.recommendation.decision.replaceAll('_', ' ')}`}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Collapse in={expanded}>
        <Stack spacing={0.5} sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
          {rows.map((row) => (
            <Typography key={row.deployment.deploymentKey} variant="caption" color="text.secondary">
              {`${row.display?.deploymentName ?? row.deployment.providerResourceName} · data ${row.dataState.replaceAll('_', ' ')} · maturity ${maturityLabel(row)} · freshness ${freshnessLabel(row)} · decision ${row.recommendation.decision.replaceAll('_', ' ')}`}
            </Typography>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function SummaryTile({
  label,
  value,
  tone = 'default',
  caption,
  testId,
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warning' | 'muted';
  /** W-A D4 (rule 7): one-line cause under the value — never a bare number. */
  caption?: string;
  testId?: string;
}): React.ReactElement {
  return (
    <Box data-testid={testId} sx={{ p: 1.5, border: 1, borderColor: tone === 'warning' ? 'warning.main' : 'divider', borderRadius: 1.5, bgcolor: tone === 'warning' ? 'warning.50' : 'background.paper' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: tone === 'muted' ? 'text.secondary' : 'text.primary' }}>{value}</Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
}

function buildColumns(
  openDecision: (row: CreativeDecisionProjection) => void,
  expanded: boolean,
  enrichmentByDeploymentKey: Readonly<Record<string, CreativeReportEnrichmentItem | undefined>>,
  enrichmentLoading: boolean,
  providerSourceByDeploymentKey: Readonly<Record<string, ProviderSourceReportEnrichmentItem | undefined>>,
  providerSourceLoading: boolean,
): SDataTableColumnDef<CreativeDecisionProjection>[] {
  if (!expanded) return buildCompactColumns(
    openDecision,
    enrichmentByDeploymentKey,
    enrichmentLoading,
    providerSourceByDeploymentKey,
    providerSourceLoading,
  );
  return [
    {
      id: 'deployment',
      header: 'Decision unit',
      size: 260,
      Cell: ({ row }) => (
        <DeploymentCell
          row={row.original}
          enrichmentItem={enrichmentByDeploymentKey[row.original.deployment.deploymentKey]}
          enrichmentLoading={enrichmentLoading}
          providerSourceItem={providerSourceByDeploymentKey[row.original.deployment.deploymentKey]}
          providerSourceLoading={providerSourceLoading}
          showLocaleChip={false}
        />
      ),
    },
    {
      id: 'locale',
      header: 'Locale',
      size: 135,
      Cell: ({ row }) => (
        <CreativeReportLocaleCell
          item={enrichmentByDeploymentKey[row.original.deployment.deploymentKey]}
          loading={enrichmentLoading}
        />
      ),
    },
    {
      id: 'state',
      header: 'Lifecycle / trust',
      size: 150,
      Cell: ({ row }) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <Chip size="small" variant="outlined" label={row.original.lifecycleStage.replaceAll('_', ' ')} />
          <Chip size="small" variant="outlined" label={row.original.dataState.replaceAll('_', ' ')} />
          <Typography variant="caption" color="text.secondary">{`Maturity: ${maturityLabel(row.original)}`}</Typography>
          <Typography variant="caption" color="text.secondary">{`Freshness: ${freshnessLabel(row.original)}`}</Typography>
        </Stack>
      ),
    },
    {
      id: 'spend',
      header: 'Spend in scope',
      size: 140,
      Cell: ({ row }) => <MoneyCell binding={row.original.money.selectedWindowSpend} basis="provider spend" />,
    },
    {
      id: 'currentFlow',
      header: 'Current money flow',
      size: 175,
      Cell: ({ row }) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 750 }}>{`${formatMoneyBinding(row.original.money.currentDailySpend, true)} / day`}</Typography>
          <Typography variant="caption" color="text.secondary">
            {`${formatPercent(row.original.money.activeSpendShare)} active share · ${formatMoneyBinding(row.original.money.unchangedPaceSevenDayExposure, true)} next 7d · ${spendTrendLabel(row.original)}`}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'valueReturned',
      header: 'Value returned',
      size: 165,
      Cell: ({ row }) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {`Net ${formatMoneyBinding(row.original.money.netCollected, true)}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`Gross ${formatMoneyBinding(row.original.money.grossCollected, true)}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`Settlement ${formatMoneyBinding(row.original.money.settlementCash, true)}`}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            {valueBasisLabel(row.original.money.netCollectedBasis ?? row.original.money.realizedValueBasis)}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'contribution',
      header: 'Contribution / projection',
      size: 195,
      Cell: ({ row }) => <ProjectionCell row={row.original} />,
    },
    {
      id: 'evidence',
      header: 'Evidence',
      size: 150,
      Cell: ({ row }) => (
        <Stack spacing={0.5} alignItems="flex-start">
          {[...new Set(row.original.evidence.map((binding) => binding.evidenceClass))].map((evidenceClass) => (
            <Chip key={evidenceClass} size="small" variant="outlined" label={evidenceClass} />
          ))}
          <FatigueChip row={row.original} />
          <Typography variant="caption" color="text.secondary">{coverageLabel(row.original)}</Typography>
        </Stack>
      ),
    },
    {
      id: 'decision',
      header: 'Recommendation',
      size: 155,
      Cell: ({ row }) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <DecisionChip decision={row.original.recommendation.decision} />
          <Typography variant="caption" color="text.secondary">
            {humanizeCreativeReasonCode(row.original.recommendation.reasonCodes[0]) ?? 'No reason'}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'action',
      header: '',
      size: 128,
      Cell: ({ row }) => (
        <Button
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={(event) => {
            event.stopPropagation();
            openDecision(row.original);
          }}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.openDecision(row.original.deployment.deploymentKey)}
        >
          Open decision
        </Button>
      ),
    },
  ];
}

function buildCompactColumns(
  openDecision: (row: CreativeDecisionProjection) => void,
  enrichmentByDeploymentKey: Readonly<Record<string, CreativeReportEnrichmentItem | undefined>>,
  enrichmentLoading: boolean,
  providerSourceByDeploymentKey: Readonly<Record<string, ProviderSourceReportEnrichmentItem | undefined>>,
  providerSourceLoading: boolean,
): SDataTableColumnDef<CreativeDecisionProjection>[] {
  return [
    {
      id: 'deployment',
      header: 'Decision unit',
      size: 230,
      Cell: ({ row }) => (
        <Stack spacing={0.75} alignItems="flex-start">
          <DeploymentCell
            row={row.original}
            enrichmentItem={enrichmentByDeploymentKey[row.original.deployment.deploymentKey]}
            enrichmentLoading={enrichmentLoading}
            providerSourceItem={providerSourceByDeploymentKey[row.original.deployment.deploymentKey]}
            providerSourceLoading={providerSourceLoading}
          />
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={(event) => {
              event.stopPropagation();
              openDecision(row.original);
            }}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.openDecision(row.original.deployment.deploymentKey)}
          >
            Open decision
          </Button>
        </Stack>
      ),
    },
    {
      id: 'state',
      header: 'Lifecycle / decision',
      size: 145,
      Cell: ({ row }) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <Chip size="small" variant="outlined" label={row.original.lifecycleStage.replaceAll('_', ' ')} />
          <Chip size="small" variant="outlined" label={row.original.dataState.replaceAll('_', ' ')} />
          <DecisionChip decision={row.original.recommendation.decision} />
          <Typography variant="caption" color="text.secondary">{`Maturity: ${maturityLabel(row.original)}`}</Typography>
          <Typography variant="caption" color="text.secondary">{`Freshness: ${freshnessLabel(row.original)}`}</Typography>
        </Stack>
      ),
    },
    {
      id: 'moneyFlow',
      header: 'Current money flow',
      size: 170,
      Cell: ({ row }) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 750 }}>
            {`${formatMoneyBinding(row.original.money.currentDailySpend, true)} / day`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`${formatMoneyBinding(row.original.money.selectedWindowSpend, true)} in scope`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`${formatPercent(row.original.money.activeSpendShare)} active · ${formatMoneyBinding(row.original.money.unchangedPaceSevenDayExposure, true)} next 7d · ${spendTrendLabel(row.original)}`}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'valueContribution',
      header: 'Value / contribution',
      size: 205,
      Cell: ({ row }) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {`Net ${formatMoneyBinding(row.original.money.netCollected, true)}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`Contribution after ads ${formatMoneyBinding(row.original.money.realizedContributionAfterAdSpend, true)}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.original.money.projectedContributionP10
              ? `Projected P10 ${formatMoneyBinding(row.original.money.projectedContributionP10, true)} · P50 ${formatMoneyBinding(row.original.money.projectedContributionP50, true)} · P90 ${formatMoneyBinding(row.original.money.projectedContributionP90, true)}`
              : 'Projected contribution n/a'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'evidence',
      header: 'Evidence',
      size: 130,
      Cell: ({ row }) => (
        <Stack spacing={0.5} alignItems="flex-start">
          {[...new Set(row.original.evidence.map((binding) => binding.evidenceClass))].map((evidenceClass) => (
            <Chip key={evidenceClass} size="small" variant="outlined" label={evidenceClass} />
          ))}
          <FatigueChip row={row.original} />
          <Typography variant="caption" color="text.secondary">{coverageLabel(row.original)}</Typography>
        </Stack>
      ),
    },
  ];
}

/**
 * Fatigue R2.6 portfolio chip: state-colored, watch-only framed. `unknown`
 * renders NOTHING (absent chip, not a gray alarm) — the absence of evidence
 * is not an alert state (DOC-4 invariant).
 */
function FatigueChip({ row }: { row: CreativeDecisionProjection }): React.ReactElement | null {
  const fatigue = row.decisionSupport?.fatigue;
  const state = fatigue?.state;
  if (!state || state === 'unknown') return null;
  const color = state === 'fatiguing' ? 'error' : state === 'watch' ? 'warning' : 'success';
  const early = state === 'watch' && fatigue?.earlyWatchReasons?.length ? ' (early window)' : '';
  return (
    <Chip
      size="small"
      color={color}
      variant="outlined"
      label={`fatigue: ${state}${early}`}
      data-testid={CREATIVE_DECISIONS_TEST_IDS.portfolioFatigueChip(state)}
    />
  );
}

function DeploymentCell({
  row,
  enrichmentItem,
  enrichmentLoading,
  providerSourceItem,
  providerSourceLoading,
  showLocaleChip = true,
}: {
  row: CreativeDecisionProjection;
  enrichmentItem?: CreativeReportEnrichmentItem;
  enrichmentLoading: boolean;
  providerSourceItem?: ProviderSourceReportEnrichmentItem;
  providerSourceLoading: boolean;
  showLocaleChip?: boolean;
}): React.ReactElement {
  const display = row.display;
  const hasExactPreview = Boolean(enrichmentItem?.enrichment?.manifest.media[0]);
  const hasProviderSourcePreview = Boolean(providerSourceItem?.source?.media.items[0]);
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      {hasExactPreview ? <CreativeReportEnrichmentThumbnail item={enrichmentItem} /> : (
        hasProviderSourcePreview ? <CreativeProviderSourceThumbnail item={providerSourceItem} /> : (
          <Avatar
            variant="rounded"
            src={display?.thumbnailUrl ?? display?.imageUrl}
            alt=""
            sx={{ width: 44, height: 44, bgcolor: 'action.selected' }}
          >
            {(display?.deploymentName ?? row.deployment.providerResourceName).slice(0, 1).toUpperCase()}
          </Avatar>
        )
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 750 }} noWrap title={display?.deploymentName}>
          {display?.deploymentName ?? row.deployment.providerResourceName}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {`${display?.decisionUnitLabel ?? row.deployment.decisionUnitKind} · ${display?.campaignName ?? display?.campaignId ?? 'campaign unavailable'}`}
        </Typography>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
          {(display?.badges ?? []).slice(0, 2).map((badge) => (
            <Chip key={badge} size="small" variant="outlined" label={badge.replaceAll('_', ' ')} sx={{ height: 18, typography: 'caption' }} />
          ))}
          {showLocaleChip && <CreativeReportLocaleCell item={enrichmentItem} loading={enrichmentLoading} compact />}
          {!hasExactPreview && hasProviderSourcePreview && (
            <Chip size="small" variant="outlined" label="synced copy" sx={{ height: 18, typography: 'caption' }} />
          )}
          {!hasExactPreview && !hasProviderSourcePreview && providerSourceLoading && (
            <Chip size="small" variant="outlined" label="copy loading" sx={{ height: 18, typography: 'caption' }} />
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

function CreativeReportLocaleCell({
  item,
  loading,
  compact = false,
}: {
  item: CreativeReportEnrichmentItem | undefined;
  loading: boolean;
  compact?: boolean;
}): React.ReactElement {
  const enrichment = item?.enrichment;
  if (enrichment) {
    const fallbackApproved = enrichment.manifest.fallbackApproved;
    return (
      <Stack spacing={0.25} alignItems="flex-start">
        <Chip
          size="small"
          variant="outlined"
          color={fallbackApproved ? 'warning' : 'default'}
          label={enrichment.manifest.locale}
          sx={compact ? { height: 18, typography: 'caption' } : undefined}
        />
        {!compact && (
          <Typography variant="caption" color="text.secondary">
            {fallbackApproved ? 'approved fallback' : 'exact export binding'}
          </Typography>
        )}
      </Stack>
    );
  }
  const resolvedUnavailable = Boolean(item);
  const label = !resolvedUnavailable && loading ? 'Locale loading' : 'Locale unknown';
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      title={resolvedUnavailable ? (item?.reason ?? 'binding unavailable').replaceAll('_', ' ') : undefined}
      sx={compact ? { height: 18, typography: 'caption' } : undefined}
    />
  );
}

function MoneyCell({
  binding,
  basis,
}: {
  binding: MoneyAmountBinding | undefined;
  basis: string;
}): React.ReactElement {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 750 }}>{formatMoneyBinding(binding, true)}</Typography>
      <Typography variant="caption" color="text.secondary">{basis}</Typography>
    </Box>
  );
}

function ProjectionCell({ row }: { row: CreativeDecisionProjection }): React.ReactElement {
  const money = row.money;
  const hasProjection = money.projectedContributionP10 || money.projectedContributionP50 || money.projectedContributionP90;
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 750 }}>
        {`Before ads ${formatMoneyBinding(money.realizedContributionBeforeAdSpend, true)}`}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {`After ads ${formatMoneyBinding(money.realizedContributionAfterAdSpend, true)}`}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {hasProjection
          ? `P10 ${formatMoneyBinding(money.projectedContributionP10, true)} · P50 ${formatMoneyBinding(money.projectedContributionP50, true)} · P90 ${formatMoneyBinding(money.projectedContributionP90, true)}`
          : 'projection n/a'}
      </Typography>
      {hasProjection && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {`pROAS P10 ${formatRoas(money.projectedContributionRoasP10)} · P50 ${formatRoas(money.projectedContributionRoasP50)} · P90 ${formatRoas(money.projectedContributionRoasP90)}`}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The out-of-scope segment renders ONLY from the server echo
 * (`sourceCoverage.outOfScopeRows` / `.deploymentScope`) — the client never
 * derives the number from row math; old cached payloads without the fields
 * render the footer byte-identical to the pre-scope build.
 */
function coverageFooter(coverage: CreativeDecisionPortfolioResult['sourceCoverage']): string {
  const base = `${coverage.providerRows.toLocaleString()} provider deployments · ${coverage.firstPartyMatchedRows.toLocaleString()} with first-party matches · ${coverage.reconciledSpendRows.toLocaleString()} spend-reconciled`;
  if (coverage.outOfScopeRows === undefined || coverage.outOfScopeRows === 0) return base;
  const visibility = coverage.deploymentScope === 'all' ? 'shown' : 'hidden';
  return `${base} · ${coverage.outOfScopeRows.toLocaleString()} outside web funnels (${visibility})`;
}

function coverageLabel(row: CreativeDecisionProjection): string {
  const coverages = row.evidence.map((binding) => binding.coverage).filter(Boolean);
  if (coverages.length === 0) return 'coverage unavailable';
  const numerator = coverages.reduce((sum, coverage) => sum + (coverage?.numerator ?? 0), 0);
  const denominator = coverages.reduce((sum, coverage) => sum + (coverage?.denominator ?? 0), 0);
  return denominator > 0 ? `${Math.round((numerator / denominator) * 100)}% bound` : 'coverage unavailable';
}

function freshnessLabel(row: CreativeDecisionProjection): string {
  if (row.evidence.some((binding) => binding.freshnessState === 'stale')) return 'stale';
  if (row.evidence.some((binding) => binding.freshnessState === 'unknown')) return 'unknown';
  const expiry = row.freshness.expiresAt ? Date.parse(row.freshness.expiresAt) : Number.NaN;
  const asOf = Date.parse(row.freshness.asOf);
  if (Number.isFinite(expiry) && Number.isFinite(asOf) && expiry <= asOf) return 'stale';
  return row.freshness.sourceRevisionRefs.length > 0 ? 'fresh' : 'unknown';
}

function maturityLabel(row: CreativeDecisionProjection): string {
  if (row.maturity?.state) return row.maturity.state;
  if (row.dataState === 'too_young') return 'immature';
  const due = row.decisionSupport?.predictionTrust?.targetMaturityDueAt;
  if (due && Number.isFinite(Date.parse(due)) && Number.isFinite(Date.parse(row.freshness.asOf))) {
    return Date.parse(row.freshness.asOf) >= Date.parse(due) ? 'mature' : 'immature';
  }
  const states = (row.stageMetrics ?? []).map((metric) => metric.maturity);
  if (states.length > 0 && states.every((state) => state === 'mature')) return 'mature';
  if (states.some((state) => state === 'immature')) return 'immature';
  return 'unknown';
}

function spendTrendLabel(row: CreativeDecisionProjection): string {
  const trend = row.money.currentDailySpendTrend;
  if (!trend || trend.direction === 'unavailable') return 'trend n/a';
  const delta = trend.changeRatio === undefined ? '' : ` ${formatPercent(Math.abs(trend.changeRatio))}`;
  return `${trend.direction}${delta} vs ${trend.comparisonWindow}`;
}

function filterLoadedRows(
  rows: CreativeDecisionProjection[],
  search: string,
): CreativeDecisionProjection[] {
  const needle = search.trim().toLocaleLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => [
    row.display?.deploymentName,
    row.display?.campaignName,
    row.display?.campaignId,
    row.display?.accountName,
    row.display?.artifactName,
    row.deployment.provider,
    row.deployment.providerAccountId,
    row.deployment.providerResourceName,
    row.deployment.providerCreativeOrAssetId,
    row.deployment.deploymentKey,
  ].some((value) => value?.toLocaleLowerCase().includes(needle)));
}

function parseCommaList(value: string): string[] {
  return [...new Set(value.split(',').map((item) => item.trim()).filter(Boolean))];
}

function humanize(value: string): string {
  return value.replaceAll('_', ' ');
}
